import React from 'react';
import { ArrowLeft, FileText, Gavel, Scale, Clock, ShieldCheck, ChevronRight } from 'lucide-react';

interface TermsOfUseProps {
  onNavigate: (view: 'home' | 'pilot' | 'privacy' | 'terms') => void;
}

export const TermsOfUse: React.FC<TermsOfUseProps> = ({ onNavigate }) => {
  return (
    <div className="bg-brandBg min-h-screen text-zodiac font-sans selection:bg-accent selection:text-white">
      {/* Header Section */}
      <section className="pt-40 pb-20 px-6 md:px-12 max-w-5xl mx-auto">
        <button 
          onClick={() => onNavigate('home')}
          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.4em] text-zodiac/30 hover:text-accent transition-colors mb-12 group"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>
        
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-zodiac leading-none mb-6">Terms of Use</h1>
            <p className="text-xl text-zodiac/40 font-light">Last Updated: June 23, 2024</p>
          </div>
          <div className="flex items-center gap-4 px-6 py-4 bg-white border border-zodiac/5 rounded-2xl shadow-sm">
            <Scale className="w-5 h-5 text-accent" />
            <div className="text-left">
              <p className="text-[10px] font-black uppercase tracking-widest text-zodiac/30 leading-none mb-1">Agreement</p>
              <p className="text-xs font-bold text-zodiac uppercase tracking-widest">Legal Framework v2.1</p>
            </div>
          </div>
        </div>
        
        <div className="w-full h-px bg-zodiac/10 mb-20"></div>

        {/* Introduction */}
        <div className="max-w-4xl space-y-16">
          <div className="prose prose-lg text-zodiac/80 font-light leading-relaxed space-y-8">
            <h2 className="text-2xl font-bold text-zodiac tracking-tight">Agreement To Terms</h2>
            <p>
              These Terms of Use ("Terms") constitute a legally binding agreement made between you, 
              whether personally or on behalf of an entity ("you") and Talent Worldwide Inc. and its 
              subsidiaries and affiliates (collectively, "Company", "we", "us", or "our"), concerning your access 
              to and use of the <span className="text-accent font-medium">www.groflex.ai</span> platform as well as any other website, including 
              groflex.io, groflex.in, groflextech.com, media channel, mobile site or mobile application related, 
              linked, or otherwise connected thereto (collectively, the "Platform").
            </p>
            <p className="bg-zodiac/5 p-8 rounded-3xl border border-zodiac/5 text-zodiac font-medium">
              IF YOU DO NOT AGREE WITH ALL OF THESE TERMS (INCLUDING ANY SUPPLEMENTAL 
              TERMS), THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE PLATFORM AND YOU 
              MUST DISCONTINUE USE IMMEDIATELY.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 py-12 border-y border-zodiac/5">
             <div className="p-8 bg-white rounded-3xl border border-zodiac/5 shadow-sm">
                <Gavel className="w-6 h-6 text-accent mb-6" />
                <h3 className="text-lg font-bold mb-4 tracking-tight">Binding Arbitration</h3>
                <p className="text-sm text-zodiac/60 leading-relaxed font-light">
                  These terms contain a dispute resolution and arbitration provision, including a class action waiver.
                </p>
             </div>
             <div className="p-8 bg-white rounded-3xl border border-zodiac/5 shadow-sm">
                <ShieldCheck className="w-6 h-6 text-accent mb-6" />
                <h3 className="text-lg font-bold mb-4 tracking-tight">Proprietary Rights</h3>
                <p className="text-sm text-zodiac/60 leading-relaxed font-light">
                  All source code, databases, and functionality on the Platform are our proprietary property.
                </p>
             </div>
          </div>

          {/* Core Sections */}
          <div className="space-y-12">
            <section className="space-y-6">
              <h2 className="text-2xl font-bold tracking-tight text-zodiac border-l-4 border-accent pl-6">Intellectual Property Rights</h2>
              <div className="text-zodiac/70 font-light leading-relaxed space-y-4">
                <p>
                  Unless otherwise indicated, the Platform is our proprietary property and all source code, 
                  databases, functionality, software, website designs, audio, video, text, photographs, and 
                  graphics on the Platform (collectively, the "Content") and the trademarks, service marks, and 
                  logos contained therein (the "Marks") are owned by us or licensed to us.
                </p>
                <p>
                  Provided that you are eligible to use the Platform, you are granted a limited license to access 
                  and use the Platform solely for your personal, non-commercial use.
                </p>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold tracking-tight text-zodiac border-l-4 border-accent pl-6">User Representations</h2>
              <div className="text-zodiac/70 font-light leading-relaxed space-y-4">
                <p>By using the Platform, you represent and warrant that:</p>
                <ul className="grid sm:grid-cols-2 gap-4 list-none pl-0">
                  {[
                    "Registration information is true and accurate.",
                    "You will maintain the accuracy of such info.",
                    "You have the legal capacity to comply.",
                    "You are not a minor in your jurisdiction.",
                    "You won't access via automated means.",
                    "You won't use for illegal purposes."
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3 items-start text-sm">
                      <ChevronRight className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold tracking-tight text-zodiac border-l-4 border-accent pl-6">Fees and Payment</h2>
              <div className="text-zodiac/70 font-light leading-relaxed space-y-4">
                <p>
                  We accept various methods of payment including debit/credit cards and third-party providers 
                  such as PayPal. You agree to provide current, complete, and 
                  accurate purchase and account information for all purchases made via the Platform.
                </p>
                <p>
                  If your purchase is subject to recurring charges, then you consent to our charging 
                  your payment method on a recurring basis without requiring your prior approval.
                </p>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold tracking-tight text-zodiac border-l-4 border-accent pl-6">Prohibited Activities</h2>
              <div className="text-zodiac/70 font-light leading-relaxed space-y-4">
                <p>You may not access or use the Platform for any purpose other than that for which we make the Platform available. As a user, you agree not to:</p>
                <ul className="space-y-2">
                   {[
                     "Systematically retrieve data to create a collection or database.",
                     "Trick, defraud, or mislead us and other users.",
                     "Circumvent, disable, or interfere with security features.",
                     "Use the Platform in an effort to compete with us.",
                     "Sell or otherwise transfer your profile.",
                     "Harass, abuse, or harm another person."
                   ].map((item, i) => (
                     <li key={i} className="flex gap-3 items-start text-sm">
                       <span className="text-accent font-bold">•</span>
                       <span>{item}</span>
                     </li>
                   ))}
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold tracking-tight text-zodiac border-l-4 border-accent pl-6">Dispute Resolution</h2>
              <div className="text-zodiac/70 font-light leading-relaxed space-y-4">
                <p>
                  To expedite resolution and control the cost of any dispute, the Parties agree to first 
                  attempt to negotiate any Dispute informally for at least thirty (30) days before initiating arbitration.
                </p>
                <p>
                  Any dispute not subject to arbitration shall be brought and litigated exclusively in the state courts 
                  located within <span className="font-bold text-zodiac">Kent County, Delaware</span>.
                </p>
              </div>
            </section>

            {/* Supplemental Notices */}
            <div className="p-10 bg-zodiac rounded-[3rem] text-white overflow-hidden relative group">
              <div className="absolute top-0 right-0 p-12 opacity-[0.03] pointer-events-none group-hover:scale-110 transition-transform duration-[10s]">
                <FileText className="w-64 h-64 text-white" />
              </div>
              <div className="relative z-10">
                <h2 className="text-2xl font-bold mb-6 tracking-tight">EU & US SUPPLEMENTAL TERMS</h2>
                <div className="grid md:grid-cols-2 gap-12">
                  <div className="space-y-4">
                    <h3 className="text-accent font-black uppercase text-[10px] tracking-widest">EU Residents</h3>
                    <p className="text-sm text-white/60 leading-relaxed font-light">
                      Additional rights including a 14-day right of withdrawal for digital content and specific 
                      liability limitations governed by the laws of the Netherlands.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-accent font-black uppercase text-[10px] tracking-widest">US Residents</h3>
                    <p className="text-sm text-white/60 leading-relaxed font-light">
                      Specific DMCA copyright infringement notice requirements and government rights 
                      acknowledgments under FAR and DFARS.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Disclaimer & Liability */}
            <section className="space-y-6">
              <h2 className="text-2xl font-bold tracking-tight text-zodiac border-l-4 border-accent pl-6">Disclaimer</h2>
              <p className="text-sm text-zodiac/50 font-medium italic">
                THE PLATFORM IS PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. WE DISCLAIM ALL WARRANTIES, 
                EXPRESS OR IMPLIED, INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS 
                FOR A PARTICULAR PURPOSE.
              </p>
            </section>
          </div>

          {/* SMS Terms Section */}
          <div className="p-10 bg-white border border-zodiac/5 rounded-[3rem] shadow-sm">
             <h3 className="text-xl font-bold text-zodiac mb-6 tracking-tight uppercase">SMS Terms of Use</h3>
             <div className="text-sm text-zodiac/60 leading-relaxed font-light space-y-4">
                <p>When you opt-in to the service, we will send you an SMS message with an activation code to verify your phone number.</p>
                <p>You can cancel the SMS service at any time by replying <span className="font-bold text-zodiac">"STOP"</span>. For help, reply <span className="font-bold text-zodiac">"HELP"</span>.</p>
                <p>Message and data rates may apply. Messaging frequency varies.</p>
             </div>
          </div>

          {/* Footer Contact Callout */}
          <div className="pt-20 pb-32">
             <div className="p-12 bg-accent rounded-[3rem] text-white flex flex-col md:flex-row items-center justify-between gap-10 shadow-2xl shadow-accent/20">
                <div className="text-left">
                  <h3 className="text-3xl font-bold mb-4 tracking-tight">Questions?</h3>
                  <p className="text-white/80 font-light">In order to resolve a complaint or receive further information, please contact us.</p>
                </div>
                <a 
                  href="mailto:hello@groflex.ai"
                  className="px-10 py-5 bg-zodiac text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-105 transition-executive shadow-xl"
                >
                  Contact Legal
                </a>
             </div>
             
             <div className="mt-16 text-center space-y-4">
                <p className="text-[10px] font-black uppercase tracking-[0.5em] text-zodiac/20">Execution Governance Standard</p>
                <div className="flex justify-center items-center gap-6">
                   <Clock className="w-4 h-4 text-accent/30" />
                   <span className="text-xs font-bold text-zodiac/30 tracking-tight">Compliance Reference: TOU-2024.6.23</span>
                </div>
             </div>
          </div>
        </div>
      </section>
    </div>
  );
};